import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useServiceTypeGroups } from '@/composables/useServiceTypeGroups'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

/**
 * Resolves service type ids (as stored on a subscription type) to
 * "<category> <duration>" labels such as "бачата 1ч", the same wording the
 * service types table uses; types that share a category and duration get their
 * description appended. Each catalog is only fetched when the viewer holds
 * its view role; unknown ids fall back to `Service type #<id>`.
 */
export function useCoveredTypeLabels() {
  const { t } = useI18n()
  const { hasRole } = usePermissions()

  const { groups } = useServiceTypeGroups({
    typesEnabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesView)),
    categoriesEnabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceNamesView)),
  })

  const labelById = computed<Map<string, string>>(() => {
    const base = new Map<string, string>()
    const count = new Map<string, number>()
    for (const group of groups.value) {
      for (const type of group.types) {
        const label = [group.categoryName, type.duration].filter(Boolean).join(' ')
        base.set(type.id, label)
        count.set(label, (count.get(label) ?? 0) + 1)
      }
    }
    // Several service types can share a category and duration (they differ by
    // description); tell those apart so the tags do not look duplicated.
    const map = new Map<string, string>()
    for (const group of groups.value) {
      for (const type of group.types) {
        const label = base.get(type.id)!
        map.set(type.id, (count.get(label) ?? 0) > 1 ? `${label} · ${type.name}` : label)
      }
    }
    return map
  })

  function labelsFor(ids: string[]): string[] {
    return ids.map(
      (id) => labelById.value.get(id) ?? t('tabs.services.serviceTypeFallback', { id }),
    )
  }

  return { labelsFor }
}
